import wretch from 'wretch';
import CookieService from './cookie-service';
import { sleepFor } from './helpers';

export default class AccountService {
  /**
   * @param {string} authToken
   * @returns Promise<Object|*>
   */
  static fetchUserInfo(authToken) {
    return wretch(`${process.env.AUTH_API}api/session/userinfo`)
      .auth(`Bearer ${authToken}`)
      .options({ credentials: 'same-origin' })
      .get()
      .json()
      .catch((e) => {
        AccountService.deleteAuthToken();
        console.error('An error occurred while fetching user info: ', e);
        throw new Error(e);
      });
  }

  /**
   * @param {string} key
   * @returns {string|null}
   */
  static getAuthToken(key) {
    return CookieService.getCookie(key || process.env.LOGIN_COOKIE);
  }

  static deleteAuthToken(key) {
    CookieService.deleteCookie(key || process.env.LOGIN_COOKIE);
  }

  /**
   * @param {Object} value
   * @param {number} timestamp
   * @returns {undefined}
   */
  static storeUserInfo(value, authToken, timestamp = Date.now()) {
    window.localStorage.setItem('UserSession', JSON.stringify({ timestamp, authToken, value }));
  }

  /**
   * @returns {undefined}
   */
  static removeUserInfo() {
    window.localStorage.removeItem('UserSession');
  }

  /**
   * @param {boolean} withFetch
   * @returns {{data: Object, timeStamp: number}|null}
   */
  static async getUserInfo(withFetch = false) {
    /**
     * @param {Object} session
     * @param {number} expirationInterval – number of milliseconds
     * @returns {boolean}
     */
    const isUserSessionValid = (
      session,
      authToken,
      expirationInterval = 60 * 5 * 1000,
    ) => (
      session
      && session.value
      && session.timestamp
      && ((Date.now() - session.timestamp) < expirationInterval)
      && session.authToken
      && session.authToken === authToken);

    /**
     * @param {Object} session
     */
    const isUserSessionExists = (session) => Boolean(session && session.value && session.timestamp);

    /**
     * @param {Object} data
     * @param {number} timeStamp
     */
    const createUserInfoObject = (data, timeStamp) => ({ data, timeStamp });

    const waitForUserInfo = () => new Promise((resolve) => {
      let index = 1;
      const interval = setInterval(() => {
        if (index > 3) {
          clearInterval(interval);
          resolve(null);
          return;
        }

        try {
          const session = JSON.parse(window.localStorage.getItem('UserSession'));

          if (session) {
            clearInterval(interval);
            resolve(session.value);
          } else {
            index += 1;
          }
        } catch (e) {
          console.error('An error occurred while trying to parse userInfo', e);
        }
      }, 1000);
    });

    let session;

    try {
      session = JSON.parse(window.localStorage.getItem('UserSession'));
    } catch (e) {
      session = null;
    }

    const authToken = AccountService.getAuthToken();

    if (authToken && isUserSessionValid(session, authToken)) {
      return createUserInfoObject(session.value, session.timestamp);
    }

    AccountService.removeUserInfo();

    if (authToken && withFetch) {
      try {
        const actualData = await AccountService.fetchUserInfo(authToken);
        const newTimeStamp = Date.now();

        AccountService.storeUserInfo(actualData, authToken, newTimeStamp);

        return createUserInfoObject(actualData, newTimeStamp);
      } catch (e) {
        if (isUserSessionExists(session)) {
          return createUserInfoObject(session.value, session.timestamp);
        }

        throw e;
      }
    } else if (authToken) {
      const actualData = await waitForUserInfo();

      if (actualData) {
        const newTimeStamp = Date.now();
        return createUserInfoObject(actualData, newTimeStamp);
      }
    }

    if (session) {
      AccountService.removeUserInfo();
    }

    return null;
  }

  static async getUserData(withFetch = false) {
    let userData;
    try {
      userData = (await AccountService.getUserInfo(withFetch)).data;
    } catch (e) {
      console.error(`An error occurred during gathering of user info: ${e}`);
      userData = null;
    }
    return userData;
  }

  /**
   * @param {string} userId
   * @returns {Object|null}
   */
  static async getUserInfoApi() {
    // is the user Authenticated
    const isAuthenticated = await this.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    // do we have an auth token
    const authToken = AccountService.getAuthToken();
    if (!authToken) {
      return null;
    }

    // get the user id from the fetch user info
    let userData = null;
    try {
      userData = await AccountService.fetchUserInfo(authToken);
    } catch (e) {
      console.error('An error occurred while trying to get user details: ', e);
      return null;
    }

    if (!userData) {
      return null;
    }

    // if here let's request a fresh copy of user data from the new API
    try {
      return (await wretch(`/web3/api/users/${userData.id}/profile`)
        .options({ credentials: 'same-origin' })
        .get()
        .json());
    } catch (e) {
      console.error('An error occurred while trying to get user profile: ', e);
      return null;
    }
  }

  /**
   * @returns {boolean|null}
   */
  static async isAuthenticated() {
    let isAuthenticated = await AccountService.getIsAuthenticated();

    if (!isAuthenticated && AccountService.getAuthToken()) {
      isAuthenticated = await AccountService.tryReauthenticate();
    }

    return isAuthenticated;
  }

  static async tryReauthenticate() {
    await AccountService.invokeAccountLoginIframe();
    await sleepFor(3000);
    const isAuthenticated = await AccountService.getIsAuthenticated();
    return isAuthenticated;
  }

  static async getIsAuthenticated() {
    try {
      return (await wretch('/web3/api/isauthenticated')
        .options({ credentials: 'same-origin' })
        .get()
        .json())
        .isAuthenticated;
    } catch (e) {
      console.error('An error occurred while trying to authenticate the user: ', e);
      return null;
    }
  }

  /**
   * @param {string} userId
   * @returns {boolean|null}
   */
  static async isPremiumUser(userId) {
    try {
      return (await wretch(`/web3/api/users/${userId}/ispremiumuser`)
        .options({ credentials: 'same-origin' })
        .get()
        .json())
        .isPremium;
    } catch (e) {
      console.error('An error occurred while trying to check the user\'s subscription: ', e);

      return null;
    }
  }

  static invokeAccountLoginIframe() {
    let loaded = false;
    const iframe = document.createElement('iframe');

    return new Promise((resolve, reject) => {
      if (loaded) {
        AccountService.removeAccountLoginIframe(iframe);
        resolve({ resource: 'web30AccountLogin', loaded: true, status: 'Already Loaded' });
      } else {
        iframe.setAttribute('id', 'web30AccountLogin');
        iframe.setAttribute('src', `/account/login?returnUrl=${window.location.origin}/blank.html`);
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';

        if (iframe.readyState) { // IE
          iframe.onreadystatechange = () => {
            if (iframe.readyState === 'loaded' || iframe.readyState === 'complete') {
              iframe.onreadystatechange = null;
              loaded = true;
              resolve({ resource: 'web30AccountLogin', loaded: true, status: 'Loaded' });
            }
          };
        } else { // Others
          iframe.onload = () => {
            loaded = true;
            AccountService.removeAccountLoginIframe(iframe);
            resolve({ resource: 'web30AccountLogin', loaded: true, status: 'Loaded' });
          };
        }
        /* eslint prefer-promise-reject-errors: ["error", {"allowEmptyReject": true}] */
        iframe.onerror = () => reject();
        document.body.appendChild(iframe);
      }
    });
  }

  static removeAccountLoginIframe(iframe) {
    setTimeout(() => {
      try {
        iframe.parentNode.removeChild(iframe);
      } catch (error) {
        // no time to explain, just trust me and let me move on
      }
    }, 5000);
  }
}
